/**
 * Client Initializer
 *
 * MANDATORY FEATURE: Initialize error tracking on client
 * Sets up axios interceptor for automatic error logging
 * Initializes console logger for capturing all console logs
 *
 * Created: 2025-10-27
 */

"use client";

import { useEffect } from "react";

export default function ClientInitializer() {
  useEffect(() => {
    const runWhenIdle = (callback: () => void) => {
      if (typeof window === "undefined") return;
      const browserWindow = window as Window &
        typeof globalThis & {
          requestIdleCallback?: (
            _callback: IdleRequestCallback,
            _options?: IdleRequestOptions
          ) => number;
          cancelIdleCallback?: (_handle: number) => void;
        };
      if (typeof browserWindow.requestIdleCallback === "function") {
        const idleId = browserWindow.requestIdleCallback(callback, {
          timeout: 3000,
        });
        return () => browserWindow.cancelIdleCallback?.(idleId);
      }
      const timeoutId = browserWindow.setTimeout(callback, 1500);
      return () => browserWindow.clearTimeout(timeoutId);
    };

    const cancel = runWhenIdle(() => {
      void Promise.all([
        import("@/lib/utils/axiosErrorInterceptor"),
        import("@/lib/utils/consoleLogger"),
      ]).then(([axiosModule, loggerModule]) => {
        // IMPORTANT: Initialize console logger before axios error tracking.
        loggerModule.consoleLogger.init();
        axiosModule.setupAxiosErrorInterceptor();
      });

      if ("serviceWorker" in navigator) {
        void navigator.serviceWorker.register("/sw.js").catch((error) => {
          console.warn("Service worker kaydı başarısız", error);
        });
      }
    });

    return cancel;
  }, []);

  return null; // This component renders nothing
}
